import {
  FunctionComponent,
  forwardRef,
  Ref,
  PropsWithoutRef,
  SyntheticEvent,
  useState,
  useEffect,
  useMemo
} from 'react';
import styled, { css } from 'styled-components';
import { readableColor, rgba } from 'polished';

import Image from '../Image';
import {
  BaseProps,
  ForwardProps,
  PropsWithDefaults,
  NoChildrenProp,
  OmitStrict
} from '../../types';
import { defaultThemeProp } from '../../theme';
import Icon, { StyledIcon } from '../Icon';
import { Alert } from '../Badges';
import { useI18n } from '../../hooks';
import { tryCatch } from '../../utils';

export interface AvatarProps extends BaseProps, NoChildrenProp {
  /** The name of the identity the Avatar represents. */
  name: string;
  /** Content of the Avatar as an image source url. */
  imageSrc?: string;
  /** An icon to render as the content of the avatar. imageSrc will take precedence if passed. */
  icon?: string;
  /**
   * Shape of the Avatar.
   * @default "circle"
   */
  shape?: 'circle' | 'squircle';
  /**
   * Avatar size.
   * @default "m"
   */
  size?: 's' | 'm' | 'l';
  /** If defined, will render status icon on the avatar. */
  status?: 'active';
  /** Optional error handler in the event an image fails to load. */
  onImageError?: (err: SyntheticEvent<HTMLImageElement>) => void;
  /** Ref for the wrapping element. */
  ref?: Ref<HTMLDivElement | HTMLImageElement>;
}

type AvatarPropsWithDefaults = PropsWithDefaults<AvatarProps, 'shape' | 'size'>;

const sizeToRem = {
  s: 1,
  m: 2,
  l: 2.5
};

const fontSizeToRem = {
  s: 0.5625,
  m: 0.8125,
  l: 1
};

const sizeToBorderRadius = {
  s: 0.25,
  m: 0.5,
  l: 1
};

export const StyledAvatarImage = styled(Image)`
  min-height: 100%;
  min-width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: inherit;
`;

export const StyledAvatar = styled.div<
  OmitStrict<AvatarPropsWithDefaults, 'name' | 'children'> & {
    hasSrc?: boolean;
    hasBrokenImage?: boolean;
  }
>(
  ({
    hasBrokenImage,
    hasSrc,
    size,
    shape,
    theme: {
      base: {
        palette: { 'foreground-color': foregroundColor, 'primary-background': primaryBackground }
      },
      components: { avatar }
    }
  }) => {
    const sizeRem = sizeToRem[size];
    const fontSize = fontSizeToRem[size];
    const backgroundColor =
      hasSrc && !hasBrokenImage ? primaryBackground : avatar['background-color'];
    const shadowColor = tryCatch(() => rgba(foregroundColor, 0.1));
    const color = tryCatch(() => readableColor(avatar['background-color']));

    return css`
      position: relative;
      background-color: ${backgroundColor};
      color: ${color};
      width: ${sizeRem}rem;
      height: ${sizeRem}rem;
      min-width: ${sizeRem}rem;
      min-height: ${sizeRem}rem;
      font-size: ${fontSize}rem;
      text-transform: uppercase;
      line-height: ${sizeRem}rem;
      text-align: center;
      vertical-align: top;
      display: inline-block;
      border-radius: 100%;
      user-select: none;
      -webkit-user-select: none;

      ${shape === 'squircle' &&
      css`
        border-radius: calc(${sizeToBorderRadius[size]} * ${avatar['border-radius']});
      `}

      ${StyledIcon} {
        width: 50%;
        height: 100%;
        vertical-align: initial;
      }

      ::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: inherit;
        pointer-events: none;
        box-shadow: inset 0 0 0 0.0625rem ${shadowColor};
      }
    `;
  }
);

StyledAvatar.defaultProps = defaultThemeProp;

export const StyledStatus = styled.div<Pick<AvatarPropsWithDefaults, 'size'>>(({ size, theme }) => {
  /** Scales alert badge dimensions based on size of avatar */
  const sizeDimensions = (0.625 * sizeToRem[size]) / sizeToRem.m;

  return css`
    position: absolute;
    height: ${sizeDimensions}rem;
    width: ${sizeDimensions}rem;
    inset-block-end: 0;
    inset-inline-end: 0;
    z-index: 1;
    animation: pop calc(${theme.base.animation.speed} * 2) cubic-bezier(1, 1.65, 0.3, 1.24);

    @keyframes pop {
      0% {
        opacity: 0;
        transform: scale(0);
      }
      50% {
        opacity: 1;
      }
      100% {
        transform: scale(1);
      }
    }
  `;
});

StyledStatus.defaultProps = defaultThemeProp;

/**
 * Helper function to construct a two or one letter initial from the title prop.
 * This text will be displayed if no valid image source is provided.
 * @param name
 * @param size
 */
const getInitials = (name: AvatarProps['name'], size: AvatarProps['size']) => {
  const words = name.split(' ');
  const firstInitial = words[0].slice(0, 1);

  return words.length === 1 || size === 's'
    ? firstInitial
    : `${firstInitial}${words[1].slice(0, 1)}`;
};

/**
 * Generally, avatars are used to distinguish between different operators and
 * objects when they are shown in a list or in a small space. Users of applications
 * can upload a custom avatar of their choice.
 */
const Avatar: FunctionComponent<AvatarProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<AvatarProps>, ref: Ref<HTMLDivElement | HTMLImageElement>) => {
    const {
      shape = 'circle',
      size = 'm',
      imageSrc,
      icon,
      name,
      status,
      onImageError,
      ...restProps
    } = props;
    const [hasBrokenImage, setHasBrokenImage] = useState<undefined | boolean>();
    const t = useI18n();

    useEffect(() => {
      setHasBrokenImage(undefined);
    }, [imageSrc]);

    const content = useMemo(() => {
      if (icon) {
        return <Icon name={icon} />;
      }
      if (imageSrc && !hasBrokenImage) {
        return (
          <StyledAvatarImage
            src={imageSrc}
            alt={name}
            onError={(e: SyntheticEvent<HTMLImageElement>) => {
              setHasBrokenImage(true);
              onImageError?.(e);
            }}
            onLoad={() => {
              setHasBrokenImage(false);
            }}
          />
        );
      }
      return getInitials(name, size);
    }, [imageSrc, name, size, icon, hasBrokenImage, onImageError]);

    return (
      <StyledAvatar
        role='img'
        hasSrc={!!imageSrc}
        hasBrokenImage={hasBrokenImage}
        shape={shape}
        size={size}
        ref={ref}
        aria-label={`${name}${status ? ` ${t('status')} ${status}` : ''}`}
        {...restProps}
      >
        {content}
        {status === 'active' && <Alert as={StyledStatus} size={size} variant='success' />}
      </StyledAvatar>
    );
  }
);

export default Avatar;
